import React, { useState, useEffect } from "react";
import Table from "react-bootstrap/Table";
import Card from "react-bootstrap/Card";
import Accordion from "react-bootstrap/Accordion";
import Tabs from "react-bootstrap/Tabs";
import Tab from "react-bootstrap/Tab";
import backendAPI from "../../../utils/backendAPI";
import Loader from "../../../components/Loader";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

function MerchantsData() {
  const [merchants, setMerchants] = useState([]);
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { t } = useTranslation();

  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;
  const isAdmin = userInfo?.is_admin || false;

  useEffect(() => {
    fetchAllMerchants();
  }, []);

  // Auto-select merchant if there's only one (for regular users)
  useEffect(() => {
    if (merchants.length === 1 && !selectedMerchant) {
      setSelectedMerchant(merchants[0]);
    }
  }, [merchants, selectedMerchant]);

  const fetchAllMerchants = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await backendAPI.get("/api/square/merchants/all/");
      if (res.status === 200) {
        setMerchants(res.data.merchants || []);
        // Auto-select if only one merchant (for regular users)
        if (res.data.merchants && res.data.merchants.length === 1) {
          setSelectedMerchant(res.data.merchants[0]);
        } else {
          setSelectedMerchant(null);
        }
      }
    } catch (e) {
      if (e.response) {
        // Server responded with an error
        if (e.response.status === 401) {
          setError("Authentication failed. Please log in again.");
        } else if (e.response.status === 404) {
          setError(
            "Endpoint not found. Make sure the backend server is running and the endpoint exists.",
          );
        } else if (e.response.data?.detail) {
          setError(`Error: ${e.response.data.detail}`);
        } else if (e.response.data?.error) {
          setError(`Error: ${e.response.data.error}`);
        } else {
          setError(
            `Server error (${e.response.status}): ${e.response.statusText || "Unknown error"}`,
          );
        }
      } else if (e.request) {
        // Request was made but no response received
        setError(
          "No response from server. Check if backend is running at " +
            process.env.REACT_APP_API_URL,
        );
      } else {
        // Error setting up the request
        setError(
          `Request error: ${e.message || "Failed to fetch merchants. Please check your connection."}`,
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch (e) {
      return dateString;
    }
  };

  const formatPrice = (amount) => {
    if (amount === null || amount === undefined) return "$0.00";
    return `$${parseFloat(amount).toFixed(2)}`;
  };

  return (
    <div className="content-page">
      <div className="content">
        <div className="container-fluid">
          <br />
          <div className="page-title-and-bnt">
            <h1>{t("merchantsData.title")}</h1>
          </div>
          <br />

          {loading ? (
            <Loader />
          ) : error ? (
            <div className="alert alert-danger">{error}</div>
          ) : (
            <>
              <div className="mb-3">
                <strong>
                  {isAdmin
                    ? t("merchantsData.paymentDetail.TotalMerchants", {
                        number: merchants.length,
                      })
                    : t("merchantsData.mySquareMerchant")}
                </strong>
              </div>
              {merchants.length > 0 ? (
                <div className="row">
                  {/* Show merchant list only for admins or if there are multiple merchants */}
                  {(isAdmin || merchants.length > 1) && (
                    <div className="col-md-4">
                      <Card>
                        <Card.Header>
                          <h5>
                            {isAdmin
                              ? t("merchantsData.merchantsList")
                              : t("merchantsData.selectMerchant")}
                          </h5>
                        </Card.Header>
                        <Card.Body
                          style={{ maxHeight: "600px", overflowY: "auto" }}
                        >
                          {merchants.map((merchant) => (
                            <Card
                              key={merchant.id}
                              className="mb-2"
                              style={{
                                cursor: "pointer",
                                border:
                                  selectedMerchant?.id === merchant.id
                                    ? "2px solid #007bff"
                                    : "1px solid #dee2e6",
                              }}
                              onClick={() => setSelectedMerchant(merchant)}
                            >
                              <Card.Body>
                                <h6>
                                  {merchant.business_name ||
                                    merchant.merchant_id}
                                </h6>
                                <small className="text-muted">
                                  {t("merchantsData.payments")}:{" "}
                                  {merchant.payments_count} |{" "}
                                  {t("dashboard.orders")}:{" "}
                                  {merchant.orders_count}
                                </small>
                              </Card.Body>
                            </Card>
                          ))}
                        </Card.Body>
                      </Card>
                    </div>
                  )}
                  <div
                    className={
                      isAdmin || merchants.length > 1 ? "col-md-8" : "col-md-12"
                    }
                  >
                    {selectedMerchant ? (
                      <MerchantDetails
                        merchant={selectedMerchant}
                        formatDate={formatDate}
                        formatPrice={formatPrice}
                      />
                    ) : (
                      <Card>
                        <Card.Body>
                          <p className="text-muted">
                            {t("merchantsData.viewDetails")}
                          </p>
                        </Card.Body>
                      </Card>
                    )}
                  </div>
                </div>
              ) : (
                <div className="alert alert-info">
                  {isAdmin
                    ? t("merchantsData.notFound")
                    : t("merchantsData.noSquareMerchant")}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function MerchantDetails({ merchant, formatDate, formatPrice }) {
  const { t } = useTranslation();
  return (
    <Card>
      <Card.Header>
        <h4>{merchant.business_name || merchant.merchant_id}</h4>
        <small className="text-muted">
          {t("merchantsData.merchantDetail.id")}: {merchant.merchant_id} |{" "}
          {t("merchantsData.country")}: {merchant.country} |{" "}
          {t("merchantsData.merchantDetail.currency")}: {merchant.currency}
        </small>
      </Card.Header>
      <Card.Body>
        <div className="mb-3">
          <strong>{t("merchantsData.merchantDetail.statistics")}</strong>
          <ul>
            <li>
              {t("merchantsData.merchantDetail.totalPayments")}:{" "}
              {merchant.payments_count}
            </li>
            <li>
              {t("merchantsData.merchantDetail.totalOrders")}:{" "}
              {merchant.orders_count}
            </li>
            {merchant.username && (
              <li>
                {t("merchantsData.merchantDetail.user")}: {merchant.username}
              </li>
            )}
          </ul>
        </div>

        <Tabs defaultActiveKey="payments" className="mb-3">
          <Tab
            eventKey="payments"
            title={`${t("merchantsData.payments")} (${merchant.payments?.length || 0})`}
          >
            {merchant.payments && merchant.payments.length > 0 ? (
              <Accordion>
                {merchant.payments.map((payment, index) => (
                  <Accordion.Item eventKey={index.toString()} key={payment.id}>
                    <Accordion.Header>
                      <div
                        style={{
                          width: "100%",
                          display: "flex",
                          justifyContent: "space-between",
                          paddingRight: "20px",
                        }}
                      >
                        <span>
                          <strong>
                            {t("merchantsData.merchantDetail.paymentId")}
                          </strong>{" "}
                          {payment.payment_id} |
                          <strong>
                            {" "}
                            {t("merchantsData.merchantDetail.amount")}
                          </strong>{" "}
                          {formatPrice(payment.amount_money)} |
                          <strong>
                            {" "}
                            {t("merchantsData.merchantDetail.status")}{" "}
                          </strong>{" "}
                          {payment.status}
                        </span>
                        {payment.order_id && (
                          <span className="badge bg-primary">
                            {t("merchantsData.merchantDetail.hasOrder")}
                          </span>
                        )}
                      </div>
                    </Accordion.Header>
                    <Accordion.Body>
                      <PaymentDetails
                        payment={payment}
                        formatDate={formatDate}
                        formatPrice={formatPrice}
                      />
                    </Accordion.Body>
                  </Accordion.Item>
                ))}
              </Accordion>
            ) : (
              <div className="alert alert-info">
                {t("merchantsData.merchantDetail.nopaymentsfound")}
              </div>
            )}
          </Tab>
          <Tab
            eventKey="orders"
            title={`${t("dashboard.orders")} (${merchant.orders_count || 0})`}
          >
            {merchant.payments &&
            merchant.payments.filter((p) => p.order_id).length > 0 ? (
              <Accordion>
                {merchant.payments
                  .filter((payment) => payment.order_id)
                  .map((order, index) => (
                    <Accordion.Item eventKey={index.toString()} key={order.id}>
                      <Accordion.Header>
                        <div
                          style={{
                            width: "100%",
                            display: "flex",
                            justifyContent: "space-between",
                            paddingRight: "20px",
                          }}
                        >
                          <span>
                            <strong>
                              {t("merchantsData.merchantDetail.orderId")}
                            </strong>{" "}
                            {order.order_id} |
                            <strong>
                              {" "}
                              {t("merchantsData.merchantDetail.total")}{" "}
                            </strong>{" "}
                            {formatPrice(
                              order.total_money || order.amount_money,
                            )}{" "}
                            |
                            <strong>
                              {" "}
                              {t("merchantsData.merchantDetail.status")}{" "}
                            </strong>{" "}
                            {order.status}
                          </span>
                          {order.order_items_count > 0 && (
                            <span className="badge bg-success">
                              {order.order_items_count}{" "}
                              {t("merchantsData.merchantDetail.items")}
                            </span>
                          )}
                        </div>
                      </Accordion.Header>
                      <Accordion.Body>
                        <PaymentDetails
                          payment={order}
                          formatDate={formatDate}
                          formatPrice={formatPrice}
                        />
                      </Accordion.Body>
                    </Accordion.Item>
                  ))}
              </Accordion>
            ) : (
              <div className="alert alert-info">
                {t("merchantsData.merchantDetail.noOrdersfound")}
              </div>
            )}
          </Tab>
        </Tabs>
      </Card.Body>
    </Card>
  );
}

function PaymentDetails({ payment, formatDate, formatPrice }) {
  const { t } = useTranslation();
  return (
    <div>
      <h5>{t("merchantsData.paymentDetail.header")}</h5>
      <Table striped bordered size="sm" className="mb-3">
        <tbody>
          <tr>
            <th>{t("merchantsData.paymentDetail.paymentID")}</th>
            <td>{payment.payment_id}</td>
          </tr>
          {payment.order_id && (
            <tr>
              <th>{t("merchantsData.paymentDetail.orderID")}</th>
              <td>{payment.order_id}</td>
            </tr>
          )}
          <tr>
            <th>{t("merchantsData.paymentDetail.status")}</th>
            <td>{payment.status}</td>
          </tr>
          <tr>
            <th>{t("merchantsData.merchantDetail.amount")}</th>
            <td>
              {formatPrice(payment.amount_money)} {payment.currency}
            </td>
          </tr>
          {payment.tip_money && (
            <tr>
              <th>{t("merchantsData.paymentDetail.tip")}</th>
              <td>{formatPrice(payment.tip_money)}</td>
            </tr>
          )}
          {payment.total_money && (
            <tr>
              <th>{t("merchantsData.paymentDetail.Total")}</th>
              <td>{formatPrice(payment.total_money)}</td>
            </tr>
          )}
          {payment.refunded_money > 0 && (
            <tr>
              <th>{t("merchantsData.paymentDetail.refunded")}</th>
              <td>{formatPrice(payment.refunded_money)}</td>
            </tr>
          )}
          {payment.card_brand && (
            <tr>
              <th>{t("merchantsData.paymentDetail.card")}</th>
              <td>
                {payment.card_brand} ****{payment.card_last_4}
              </td>
            </tr>
          )}
          {payment.customer_id && (
            <tr>
              <th>{t("merchantsData.paymentDetail.customerID")}</th>
              <td>{payment.customer_id}</td>
            </tr>
          )}
          {payment.buyer_email_address && (
            <tr>
              <th>{t("merchantsData.paymentDetail.email")}</th>
              <td>{payment.buyer_email_address}</td>
            </tr>
          )}
          <tr>
            <th>{t("merchantsData.paymentDetail.createdAt")}</th>
            <td>{formatDate(payment.square_created_at)}</td>
          </tr>
        </tbody>
      </Table>

      {payment.order_items && payment.order_items.length > 0 && (
        <div>
          <h5>
            {t("merchantsData.paymentDetail.orderItems")} (
            {payment.order_items.length})
          </h5>
          <Table striped bordered hover size="sm">
            <thead>
              <tr>
                <th>{t("merchantsData.paymentDetail.itemName")}</th>
                <th>{t("merchantsData.paymentDetail.quantity")}</th>
                <th>{t("merchantsData.paymentDetail.price")}</th>
                <th>{t("merchantsData.paymentDetail.itemID")}</th>
                <th>{t("merchantsData.paymentDetail.variation")}</th>
              </tr>
            </thead>
            <tbody>
              {payment.order_items.map((item) => (
                <tr key={item.id}>
                  <td>{item.name}</td>
                  <td>{item.quantity}</td>
                  <td>{formatPrice(item.price)}</td>
                  <td>{item.item_id || "N/A"}</td>
                  <td>{item.variation_name || "N/A"}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
}

export default MerchantsData;
